import AppConfig from '@/config'
import { loadScript } from './loadScript'
import type { WidgetPaymentData, WidgetResult } from '@/types/cloudpayments'
import type { WidgetPayload } from '@/api/types'

const SDK_URL = 'https://widget.cloudpayments.ru/bundles/cloudpayments.js'

/**
 * Повторы внутри виджета выключены: после отказа карты сессия обязана закончиться, а
 * человека ведёт наша шторка «Ошибка оплаты» с кнопкой «Попробовать снова». Своя шторка
 * знает про статус заказа у нашего бэка, а виджет — нет.
 */
const RETRY_PAYMENT = false

export interface WidgetSession {
	/** Была попытка списания: деньги в игре, заказ надо поллить. */
	onAttempt: (details: { transactionId?: number | string; reasonCode?: number | string }) => void
	/**
	 * Виджет закрыт. Промис дожидаемся: на отмене обработчик гасит заказ на бэке, и до
	 * его ответа сессия оплаты не считается завершённой (см. billing.cancel).
	 */
	onClose: (details: { hadAttempt: boolean }) => void | Promise<void>
}

export async function loadWidgetSdk(): Promise<void> {
	// На сборках без платного контура чужой скрипт не нужен вовсе.
	if (!AppConfig.internalB2C) throw new Error('Платёжный виджет недоступен в этой сборке')
	if (import.meta.env.VITE_AUTH_MOCK) return
	await loadScript(SDK_URL)
}

/**
 * Данные платежа из исхода. У отказа самого SDK (негодный payload) в data лежит строка
 * с причиной, а не объект, поэтому читаем поля только через эту проверку.
 */
function paymentData(result: WidgetResult): WidgetPaymentData {
	const data = result?.data
	return typeof data === 'object' && data !== null ? data : {}
}

/**
 * Была ли попытка списания, то есть надо ли опрашивать заказ.
 *
 * Решают признаки транзакции и status, а не type. Type отвечает только на вопрос «как
 * закрылось окно» и закрытым списком не является: документация знает четыре значения, но
 * у альтернативных способов оплаты приезжает имя способа — с Т-Банка это 'tinkoff'.
 * Разбор по type ровно на этом и терял платёж: исход { type: 'tinkoff', status: 'success',
 * data: { transactionId } } уходил в ветку «человек закрыл окно, не начав списание», и
 * оплаченный заказ гасился по abort.
 *
 * Из статусов «денег не было» означает один 'cancel'. Незнакомый статус считаем попыткой:
 * лишний поллинг заказа стоит нескольких запросов, а пропущенная попытка — денег человека.
 */
function isAttempt(result: WidgetResult): boolean {
	if (result?.type === 'payment' || result?.type === 'installment') return true
	const data = paymentData(result)
	if (data.transactionId !== undefined || data.ReasonCode !== undefined) return true
	return result?.status !== undefined && result.status !== 'cancel'
}

/** Причина отказа виджета для лога: у SDK это строка в data, дальше — что найдётся. */
function describeRefusal(result: WidgetResult): string {
	if (typeof result?.data === 'string' && result.data.trim()) return result.data
	const message = paymentData(result).Message
	return typeof message === 'string' && message.trim() ? message : 'причина не указана'
}

export async function openPaymentWidget(payload: WidgetPayload, session: WidgetSession): Promise<void> {
	await loadWidgetSdk()

	const result = import.meta.env.VITE_AUTH_MOCK ? await runMockWidget() : await runWidget(payload)
	const data = paymentData(result)

	// transactionId и ReasonCode логируем всегда: без них разбор жалобы на списание невозможен.
	console.warn('[billing] исход платёжного виджета', {
		type: result?.type,
		status: result?.status,
		transactionId: data.transactionId,
		reasonCode: data.ReasonCode
	})

	if (isAttempt(result)) {
		session.onAttempt({ transactionId: data.transactionId, reasonCode: data.ReasonCode })
		return
	}

	if (result?.type === 'error') {
		// Виджет отказался открываться: почти всегда это негодный payload (нет ключа
		// терминала, не та валюта). Денег не было, но и отменой это назвать нельзя —
		// человек ничего не отменял. Бросаем, чтобы причина попала в лог, а стор — в failed.
		throw new Error(`Платёжный виджет отказал: ${describeRefusal(result)}`)
	}

	await session.onClose({ hadAttempt: false })
}

/**
 * Открывает виджет и ждёт исход сессии.
 *
 * `start(…)` — вход в Intent API: бэк отдаёт payload этого поколения, с `publicTerminalId`
 * вместо `publicId`. Соседний `pay('charge', …)` ведёт в классический платёж и требует
 * именно `publicId` — на новом payload он молча падает на валидации, не открыв формы.
 *
 * Механика исхода у двух методов общая, поэтому разбор ниже от смены вызова не зависит.
 *
 * Промис резолвится один раз, когда окно закрылось, и итогом сессии. Повторов внутри
 * окна нет (`retryPayment: false`), поэтому исход всегда один — первый и единственный.
 * Реджекта у промиса нет: отказ приезжает как `type: 'error'`.
 */
async function runWidget(payload: WidgetPayload): Promise<WidgetResult> {
	const api = window.cp?.CloudPayments
	if (!api) throw new Error('Платёжный виджет не инициализирован')

	const widget = new api()
	if (typeof widget.start !== 'function') throw new Error('Платёжный виджет не поддерживает start')

	// Свои опции — после спреда: это решения фронта, и поле от бэка не должно их перебивать.
	const options = { ...payload, retryPayment: RETRY_PAYMENT }
	// Второй аргумент — объект легаси-колбэков; не передаём его, исход берём промисом.
	return await widget.start(options)
}

/** Исход обычной карты: им мок отвечает, пока не попросили другой. */
const MOCK_CARD_PAYMENT: WidgetResult = {
	type: 'payment',
	status: 'success',
	success: true,
	data: { transactionId: 'mock-tx', ReasonCode: 0 }
}

/**
 * Исходы виджета под моком. tinkoff — оплата альтернативным способом: в type приезжает
 * имя способа вместо документированного значения. Ровно этот исход фронт однажды принял
 * за отмену (см. isAttempt), поэтому он заслуживает кнопки для ручной проверки.
 */
const MOCK_RESULTS: Record<string, WidgetResult> = {
	cancel: { type: 'cancel' },
	tinkoff: { type: 'tinkoff', status: 'success', data: { transactionId: 'mock-tx' } }
}

function runMockWidget(): Promise<WidgetResult> {
	const mode = localStorage.getItem('mock_billing') ?? ''
	const result = MOCK_RESULTS[mode] ?? MOCK_CARD_PAYMENT
	console.warn(
		`💳 [MOCK] платёжный виджет (${mode || 'оплата картой'}). Исход: localStorage.mock_billing = ${Object.keys(MOCK_RESULTS).join(' | ')}`
	)
	return new Promise((resolve) => {
		setTimeout(() => resolve(result), 800)
	})
}
